/**
 * `Scenes_Executor.ts` — port of Unity's `Executor/Scenes_Executor.cs` for
 * the web-registered scene tools. Scene numbers are 1-based over
 * `IProjectModel.scenes` display order.
 */

import { Ioc } from "@core/ioc/ioc";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { SceneInfo } from "@models/data/sceneInfo";
import type { ISceneModel } from "@models/scene/ISceneModel";
import { SCENE_MODEL } from "@models/scene/ISceneModel";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  getNumber,
  getString,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class ScenesExecutor implements IDomainExecutor {
  private readonly m_project: IProjectModel;

  constructor(project: IProjectModel) {
    this.m_project = project;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.NewScene:
        return this.newScene();
      case AgentToolNames.DuplicateScene:
        return this.duplicateScene();
      case AgentToolNames.GoToScene:
        return this.goToScene(args);
      case AgentToolNames.GoToNextScene:
        return this.goToAdjacentScene(1);
      case AgentToolNames.GoToPreviousScene:
        return this.goToAdjacentScene(-1);
      case AgentToolNames.ResetSceneView:
        return this.resetSceneView();
      case AgentToolNames.RenameScene:
        return this.renameScene(args);
      case AgentToolNames.UpdateSceneNotes:
        return this.updateSceneNotes(args);
      case AgentToolNames.ReorderScene:
        return this.reorderScene(args);
      case AgentToolNames.DeleteScene:
        return this.deleteScene(args);
      default:
        return null;
    }
  }

  private sceneByNumber(sceneNumber: number): SceneInfo | null {
    const scenes = this.m_project.scenes;
    if (sceneNumber < 1 || sceneNumber > scenes.length) return null;
    return scenes[sceneNumber - 1];
  }

  private resolveSceneArg(args: ToolArgs): { scene: SceneInfo; number: number } | string {
    const scenes = this.m_project.scenes;
    const sceneNumber = getNumber(args, "sceneNumber");
    if (sceneNumber === null) {
      const active = this.m_project.activeScene;
      if (!active) return "error: no active scene";
      return { scene: active, number: scenes.findIndex((s) => s.id === active.id) + 1 };
    }
    const scene = this.sceneByNumber(sceneNumber);
    if (!scene) {
      return `error: scene number ${sceneNumber} is out of range (1..${scenes.length})`;
    }
    return { scene, number: sceneNumber };
  }

  private async newScene(): Promise<string> {
    const scene = await this.m_project.createSceneAsync();
    await this.m_project.setActiveScene(scene.id);
    const number = this.m_project.scenes.findIndex((s) => s.id === scene.id) + 1;
    return `ok: created and navigated to scene ${number} '${scene.name}'`;
  }

  private async duplicateScene(): Promise<string> {
    const active = this.m_project.activeScene;
    if (!active) return "error: no active scene";
    const copy = await this.m_project.duplicateScene(active.id);
    if (!copy) return "error: duplicate_scene_failed";
    await this.m_project.setActiveScene(copy.id);
    const number = this.m_project.scenes.findIndex((s) => s.id === copy.id) + 1;
    return `ok: duplicated to scene ${number} '${copy.name}' and navigated to it`;
  }

  private async goToScene(args: ToolArgs): Promise<string> {
    const sceneNumber = getNumber(args, "sceneNumber");
    if (sceneNumber === null) return "error: go_to_scene_missing_sceneNumber";
    const scene = this.sceneByNumber(sceneNumber);
    if (!scene) {
      return `error: scene number ${sceneNumber} is out of range (1..${this.m_project.scenes.length})`;
    }
    await this.m_project.setActiveScene(scene.id);
    return `ok: navigated to scene ${sceneNumber} '${scene.name}'`;
  }

  private async goToAdjacentScene(direction: 1 | -1): Promise<string> {
    const scenes = this.m_project.scenes;
    const active = this.m_project.activeScene;
    if (!active || scenes.length === 0) return "error: no active scene";
    const index = scenes.findIndex((s) => s.id === active.id);
    const nextIndex = index + direction;
    if (nextIndex < 0) return "ok: already on the first scene";
    if (nextIndex >= scenes.length) return "ok: already on the last scene";
    const next = scenes[nextIndex];
    await this.m_project.setActiveScene(next.id);
    return `ok: navigated to scene ${nextIndex + 1} '${next.name}'`;
  }

  /**
   * Port of `Scenes_Executor.ResetSceneView` (`Scenes_Executor.cs:497`), which
   * calls `ISceneModel.TransitionResetCamera()` — the ANIMATED return to the
   * active scene's authored view, at that scene's transition speed. (The
   * client's own reset button uses the instant `ResetCamera()` instead; the
   * agent's move is animated so the user can follow where it went.)
   *
   * Upstream's guards in order: project, active scene, scene model. The web's
   * third guard asks whether a viewport has attached its CAMERA, not whether
   * the model exists — `ISceneModel` is registered unconditionally at
   * bootstrap, so an `Ioc.isRegistered` test there is a tautology that would
   * hand the agent `"ok"` for a reset that silently never happened.
   */
  private async resetSceneView(): Promise<string> {
    if (!this.m_project.activeProject) {
      return "error: no project is currently open. Please open a project first.";
    }
    const scene = this.m_project.activeScene;
    if (!scene) return "error: no active scene";
    const sceneModel = Ioc.resolve<ISceneModel>(SCENE_MODEL);
    if (!sceneModel.isSceneViewCameraAttached) {
      return "tool_error: reset_scene_view_camera_unavailable. The 3D viewport is not active.";
    }
    sceneModel.transitionResetSceneCamera();
    return "ok: reset scene view";
  }

  private async renameScene(args: ToolArgs): Promise<string> {
    const newName = getString(args, "newName");
    if (!newName) return "error: rename_scene_missing_newName";
    const resolved = this.resolveSceneArg(args);
    if (typeof resolved === "string") return resolved;
    await this.m_project.renameScene(resolved.scene.id, newName);
    return `ok: renamed scene ${resolved.number} to '${newName}'`;
  }

  private async updateSceneNotes(args: ToolArgs): Promise<string> {
    const newNotes = getString(args, "newNotes");
    if (newNotes === null) return "error: update_scene_notes_missing_newNotes";
    const resolved = this.resolveSceneArg(args);
    if (typeof resolved === "string") return resolved;
    await this.m_project.updateScenePresenterNotes(resolved.scene.id, newNotes);
    return `ok: notes updated for scene ${resolved.number} '${resolved.scene.name}'`;
  }

  private async reorderScene(args: ToolArgs): Promise<string> {
    const newSceneNumber = getNumber(args, "newSceneNumber");
    if (newSceneNumber === null) return "error: reorder_scene_missing_newSceneNumber";
    const resolved = this.resolveSceneArg(args);
    if (typeof resolved === "string") return resolved;

    const scenes = [...this.m_project.scenes];
    const clampedTarget = Math.min(Math.max(newSceneNumber, 1), scenes.length);
    const fromIndex = resolved.number - 1;
    const toIndex = clampedTarget - 1;
    if (fromIndex === toIndex) return "ok: scene already at that position";

    const [moved] = scenes.splice(fromIndex, 1);
    scenes.splice(toIndex, 0, moved);
    await this.m_project.reorderScenes(scenes.map((s) => s.id));
    return `ok: moved scene '${moved.name}' to position ${clampedTarget}`;
  }

  private async deleteScene(args: ToolArgs): Promise<string> {
    const sceneNumber = getNumber(args, "sceneNumber");
    if (sceneNumber === null) return "error: delete_scene_missing_sceneNumber";
    const scene = this.sceneByNumber(sceneNumber);
    if (!scene) {
      return `error: scene number ${sceneNumber} is out of range (1..${this.m_project.scenes.length})`;
    }
    // A project must always keep at least one scene — the last remaining scene
    // can't be deleted (the model enforces this too; report it honestly here
    // instead of returning a false success).
    if (this.m_project.scenes.length <= 1) {
      return "error: cannot delete the only scene in the project — a project must have at least one scene";
    }
    const wasActive = this.m_project.activeScene?.id === scene.id;
    await this.m_project.deleteScene(scene.id);
    if (wasActive) {
      const remaining = this.m_project.scenes;
      if (remaining.length > 0) await this.m_project.setActiveScene(remaining[0].id);
    }
    return `ok: deleted scene ${sceneNumber} '${scene.name}'`;
  }
}
